// src/components/plan/PlanPaymentPage.jsx
// Pooled inbox SP5 — /plan/payment, Add a card for after your free month
// (proto plan/payment.html; spec § Screens per app → Web, § Server contract
// card flow). Reloadable: the plan comes from GET /billing/me `pendingPlan`,
// never from Redux or the stored user. The card is Stripe's form through the
// one adapter (StripeCardForm); the SetupIntent is the server's
// (POST /billing/setup-intent, $0), confirmed by Stripe in the page, then
// POST /billing/start-trial starts the free month and the entitlement it
// answers becomes the app's before the Plan started page opens.
//
// States: drawn — declined (Stripe's message under the card, the button comes
// back), incomplete (the button stays off), processing ("Please wait...");
// undrawn — 3-D Secure (Stripe's own window; a failed check is Stripe's
// message like a decline), Stripe unreachable (STRIPE_UNREACHABLE), already
// subscribed (→ /dashboard), no plan chosen (→ /plan), no second free month
// (R224: the price is due today).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui";
import Loader from "../Loader";
import {
  GENERIC_ERROR,
  LOAD_FAILED,
  TRY_AGAIN,
} from "../onboarding/onboardingCopy";
import {
  createSetupIntent,
  getBillingMe,
  getPlans,
  startTrial,
} from "../../shared/_services/billing.services";
import Ledger from "./Ledger";
import PlanLayout from "./PlanLayout";
import { StripeCardForm } from "./StripeCardForm";
import { PLAN_PATH, PLAN_STARTED_PATH, useBillingResult } from "./planNav";
import {
  CARD_DECLINED,
  CARD_DECLINED_WITH,
  CARD_DETAILS,
  CHANGE_PLAN,
  CONFLICT_RETRY,
  LEDGER_DUE_TODAY,
  LEDGER_FIRST_MONTH,
  LEDGER_FREE,
  LEDGER_PLAN,
  LEDGER_SETUP_FEE,
  LEDGER_THEN,
  LEDGER_THEN_RETURNING,
  LEDGER_WAIVED,
  ORDER_SUMMARY,
  PAY_FOOT,
  PAY_FOOT_RETURNING,
  PAY_TITLE,
  PAY_TITLE_RETURNING,
  PLEASE_WAIT,
  PROCESSED_BY_STRIPE,
  START_FREE_MONTH,
  START_PLAN,
  STRIPE_UNREACHABLE,
  money,
} from "./planCopy";

const DASHBOARD_PATH = "/dashboard";
const PANEL =
  "tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6";
// Stripe could not be reached (or not loaded): our wording, not Stripe's.
const UNREACHABLE = ["api_connection_error", "not_ready"];

export const orderRows = ({ name, priceCents }, freeMonth) =>
  freeMonth
    ? [
        { label: LEDGER_PLAN(name), value: money(priceCents) },
        { label: LEDGER_FIRST_MONTH, value: LEDGER_FREE },
        { label: LEDGER_SETUP_FEE, value: LEDGER_WAIVED },
        { label: LEDGER_DUE_TODAY, value: money(0), kind: "total" },
        { label: LEDGER_THEN, value: money(priceCents), kind: "then" },
      ]
    : [
        { label: LEDGER_PLAN(name), value: money(priceCents) },
        { label: LEDGER_SETUP_FEE, value: LEDGER_WAIVED },
        { label: LEDGER_DUE_TODAY, value: money(priceCents), kind: "total" },
        {
          label: LEDGER_THEN_RETURNING,
          value: money(priceCents),
          kind: "then",
        },
      ];

export default function PlanPaymentPage() {
  const navigate = useNavigate();
  const settle = useBillingResult();
  const h1 = useRef(null);
  const [load, setLoad] = useState({ status: "loading", data: null });
  const [busy, setBusy] = useState(false);
  const [cardError, setCardError] = useState(null);
  const alive = useRef(false);
  // The SetupIntent Stripe already confirmed: a retry after a failed
  // start-trial reuses it (confirming a succeeded intent again is refused).
  const confirmedSi = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const read = useCallback(async () => {
    setLoad({ status: "loading", data: null });
    try {
      const [me, list] = await Promise.all([getBillingMe(), getPlans()]);
      if (!alive.current) return;
      if (me.data.entitlement?.chat) {
        navigate(DASHBOARD_PATH, { replace: true });
        return;
      }
      if (!me.data.pendingPlan) {
        navigate(PLAN_PATH, { replace: true });
        return;
      }
      confirmedSi.current = null;
      const intent = await createSetupIntent();
      if (!alive.current) return;
      setLoad({
        status: "ready",
        data: {
          plan: me.data.pendingPlan,
          freeMonth: list.data.freeMonth,
          clientSecret: intent.data.clientSecret,
        },
      });
    } catch (err) {
      if (!alive.current) return;
      const code = err?.response?.data?.code;
      if (code === "already_entitled") {
        navigate(DASHBOARD_PATH, { replace: true });
        return;
      }
      if (code === "no_pending_plan") {
        navigate(PLAN_PATH, { replace: true });
        return;
      }
      setLoad({
        status: "error",
        data: code === "stripe_unavailable" ? STRIPE_UNREACHABLE : LOAD_FAILED,
      });
    }
  }, [navigate]);
  useEffect(() => {
    read();
  }, [read]);
  useEffect(() => {
    if (load.status === "ready") h1.current?.focus();
  }, [load.status]);

  const submit = async (confirm) => {
    setBusy(true);
    setCardError(null);
    let setupIntentId = confirmedSi.current;
    if (!setupIntentId) {
      const result = await confirm("setup");
      if (!alive.current) return;
      if (!result.ok) {
        setCardError(
          UNREACHABLE.includes(result.error.type)
            ? STRIPE_UNREACHABLE
            : result.error.message || CARD_DECLINED,
        );
        setBusy(false);
        return;
      }
      setupIntentId = result.intent.id;
      confirmedSi.current = setupIntentId;
    }
    try {
      let res;
      try {
        res = await startTrial(setupIntentId);
      } catch (err) {
        if (err?.response?.data?.code !== "conflict") throw err;
        // A concurrent change on the server: the same call is tried once more.
        res = await startTrial(setupIntentId);
      }
      await settle(res.data.entitlement);
      if (!alive.current) return;
      navigate(PLAN_STARTED_PATH, {
        replace: true,
        state: { receipt: res.data.receipt },
      });
    } catch (err) {
      if (!alive.current) return;
      const code = err?.response?.data?.code;
      if (
        code === "already_entitled" ||
        code === "already_entitled_elsewhere"
      ) {
        navigate(DASHBOARD_PATH, { replace: true });
        return;
      }
      if (code === "no_pending_plan") {
        navigate(PLAN_PATH, { replace: true });
        return;
      }
      if (code === "conflict") {
        setCardError(CONFLICT_RETRY);
        setBusy(false);
        return;
      }
      if (code === "card_declined") {
        // The saved card was refused server-side: that SetupIntent is spent,
        // so the form gets a fresh one for another card.
        confirmedSi.current = null;
        setCardError(CARD_DECLINED_WITH(err.response.data.declineCode));
        try {
          const intent = await createSetupIntent();
          if (!alive.current) return;
          setLoad((l) => ({
            ...l,
            data: { ...l.data, clientSecret: intent.data.clientSecret },
          }));
        } catch (_) {
          if (!alive.current) return;
          setCardError(STRIPE_UNREACHABLE);
        }
        setBusy(false);
        return;
      }
      setCardError(
        !err?.response || code === "stripe_unavailable"
          ? STRIPE_UNREACHABLE
          : err.response.data?.message || GENERIC_ERROR,
      );
      setBusy(false);
    }
  };

  if (load.status === "loading")
    return (
      <PlanLayout>
        <Loader />
      </PlanLayout>
    );
  if (load.status === "error")
    return (
      <PlanLayout>
        <div
          role="alert"
          className="tw-grid tw-justify-items-center tw-gap-4 tw-text-center"
        >
          <p className="tw-m-0">{load.data}</p>
          <Button variant="quiet" onClick={read}>
            {TRY_AGAIN}
          </Button>
        </div>
      </PlanLayout>
    );

  const { plan, freeMonth, clientSecret } = load.data;
  return (
    <PlanLayout>
      <h1
        ref={h1}
        tabIndex={-1}
        className="tw-mb-8 tw-mt-0 tw-text-center tw-text-2xl tw-font-bold focus:tw-outline-none"
      >
        {freeMonth ? PAY_TITLE : PAY_TITLE_RETURNING}
      </h1>
      <StripeCardForm clientSecret={clientSecret}>
        {({ field, ready, loadError, complete, confirm }) => (
          <form
            noValidate
            className="tw-grid tw-gap-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready && complete && !busy) submit(confirm);
            }}
          >
            <section aria-labelledby="plan-pay-summary" className={PANEL}>
              <h2
                id="plan-pay-summary"
                className="tw-mb-4 tw-mt-0 tw-text-lg tw-font-bold"
              >
                {ORDER_SUMMARY}
              </h2>
              <Ledger rows={orderRows(plan, freeMonth)} />
              <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-text-sm tw-text-text-muted">
                <span>{freeMonth ? PAY_FOOT : PAY_FOOT_RETURNING}</span>
                <Link
                  to={PLAN_PATH}
                  className="tw-text-link hover:tw-text-link-hover"
                >
                  {CHANGE_PLAN}
                </Link>
              </div>
            </section>
            <section className={PANEL}>
              <fieldset className="tw-m-0 tw-min-w-0 tw-border-0 tw-p-0">
                <legend className="tw-mb-4 tw-p-0 tw-text-lg tw-font-bold">
                  {CARD_DETAILS}
                </legend>
                {field}
                {(cardError || loadError) && (
                  <p
                    role="alert"
                    className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-danger"
                  >
                    {cardError || loadError}
                  </p>
                )}
              </fieldset>
            </section>
            <div className="tw-grid tw-justify-items-center tw-gap-3 tw-text-center">
              <Button
                type="submit"
                className="tw-w-full"
                disabled={!ready || !complete}
                loading={busy}
              >
                {busy ? PLEASE_WAIT : freeMonth ? START_FREE_MONTH : START_PLAN}
              </Button>
              <p role="status" className="tw-sr-only">
                {busy ? PLEASE_WAIT : ""}
              </p>
              <p className="tw-m-0 tw-text-sm tw-text-text-muted">
                {PROCESSED_BY_STRIPE}
              </p>
            </div>
          </form>
        )}
      </StripeCardForm>
    </PlanLayout>
  );
}
